import {keyToType, sItemIntToKey, sItemKeyToInt} from '../../../shared-utilities/models-old/datastructures';
import {ISearchableFields} from '../../shared-models/type-sense/default-searchable-fields';
import {
  IFacetCounts,
  IFacetFieldCounts,
  TypeSenseFacetCounts,
  TypeSenseHit,
  TypeSenseSearchResponse,
} from '../../shared-models/type-sense/type-sense-types';
import {
  FilterTypeToSymbol,
  IAdvancedFilterGroup,
  IFilter,
  IFilterGroup,
  OperatorMap,
  resolveAdvancedFilterColumnType,
} from '../../../features/settings-new/models';

/** Indexed placeholder for empty string fields in Typesense (requires symbols_to_index on the collection). */
export const TYPESENSE_EMPTY_STRING_PLACEHOLDER = '~~';

/** Shown when a text-field client-side filter (is empty / ends with) is selected in the advanced filter builder. */
export const ADVANCED_FILTER_CLIENT_SIDE_IS_EMPTY_SLOW_MESSAGE = 'This will slow down filtering.';

/** Shown when creating a stock take with a saved filter that uses text-field client-side filters. */
export const STOCK_TAKE_CLIENT_SIDE_IS_EMPTY_SLOW_MESSAGE =
  'One or more selected filters use client-side text matching (is empty / ends with). Creating this stock take may take longer than usual.';

/**
 * Returns true when the filter checks string emptiness via is empty / is not empty.
 */
export function isStringPresenceAdvancedFilter(filter: IFilter): boolean {
  if (!filter.column || (filter.filterType !== 'is empty' && filter.filterType !== 'is not empty')) {
    return false;
  }

  const resolvedType = resolveAdvancedFilterColumnType(filter.column, filter.columnType);
  return resolvedType === 'string' || resolvedType === 'string[]';
}

/**
 * Returns true when the filter is string "ends with" (Typesense filter_by has no suffix support).
 */
export function isClientSideEndsWithAdvancedFilter(filter: IFilter): boolean {
  if (!filter.column || filter.filterType !== 'ends with') {
    return false;
  }

  const resolvedType = resolveAdvancedFilterColumnType(filter.column, filter.columnType);
  return resolvedType === 'string';
}

/**
 * Returns true when the filter must be applied client-side (omitted from Typesense filter_by).
 */
export function isClientSideOnlyAdvancedFilter(filter: IFilter): boolean {
  return isStringPresenceAdvancedFilter(filter) || isClientSideEndsWithAdvancedFilter(filter);
}

/**
 * Returns true when the filter uses client-side "is empty" on a string or string[] column.
 */
export function isClientSideIsEmptyAdvancedFilter(filter: IFilter): boolean {
  if (!filter.column || filter.filterType !== 'is empty') {
    return false;
  }

  const resolvedType = resolveAdvancedFilterColumnType(filter.column, filter.columnType);
  return resolvedType === 'string' || resolvedType === 'string[]';
}

/**
 * Returns true when any filter in the group uses a client-side-only text filter that may be slow.
 */
export function hasClientSideIsEmptyAdvancedFilters(filterObject: IAdvancedFilterGroup | null | undefined): boolean {
  if (!filterObject?.filterGroup) {
    return false;
  }
  return collectAdvancedFilters(filterObject.filterGroup).some(
    (filter: IFilter): boolean => isClientSideIsEmptyAdvancedFilter(filter) || isClientSideEndsWithAdvancedFilter(filter),
  );
}

/**
 * Returns true when any filter in the group requires client-side handling.
 * Typesense cannot match indexed empty strings via placeholder/_missing alone, and has no ends-with filter.
 */
export function hasStringPresenceAdvancedFilters(filterObject: IAdvancedFilterGroup | null | undefined): boolean {
  if (!filterObject?.filterGroup) {
    return false;
  }
  return collectAdvancedFilters(filterObject.filterGroup).some(isClientSideOnlyAdvancedFilter);
}

function collectAdvancedFilters(filterGroup: IFilterGroup): IFilter[] {
  const filters: IFilter[] = [];
  filterGroup.filtersAndGroups.forEach((item: IFilter | IFilterGroup): void => {
    if ('filtersAndGroups' in item) {
      filters.push(...collectAdvancedFilters(item));
      return;
    }
    filters.push(item);
  });
  return filters;
}

/**
 * Converts an object of searchable fields into an array of field names based on their searchability.
 */
export function convertSearchableFields(query_by: ISearchableFields): string[] | string {
  const fieldArray: string[] = [];
  Object.keys(query_by).forEach((key: string): void => {
    if (query_by[key].value) {
      fieldArray.push(String(sItemKeyToInt[key]));
    }
  });
  return fieldArray.length > 0 ? fieldArray : '';
}

/**
 * Creates a map of facet counts from an array of TypeSense facet counts.
 */
export function createFacetCountsMap(facetsCount: TypeSenseFacetCounts[]): IFacetCounts {
  return facetsCount.reduce((acc, facet) => {
      acc[sItemIntToKey[facet.field_name]] = facet.counts
        .reduce((fieldAcc, {value, count}) => {
            fieldAcc[value] = count;
            return fieldAcc;
          },
          {} as IFacetFieldCounts,
        );
      return acc;
    },
    {} as IFacetCounts,
  );
}

/**
 * Converts a Typesense date field value (epoch seconds, millis, or ISO string) to a Date.
 */
function mapTypesenseValueToDate(raw: unknown): Date | unknown {
  if (raw == null) {
    return raw;
  }
  if (raw instanceof Date) {
    return raw;
  }
  if (typeof raw === 'number') {
    const ms = raw > 1_000_000_000_000 ? raw : raw * 1000;
    return new Date(ms);
  }
  if (typeof raw === 'string' && raw.length > 0) {
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed);
    }
  }
  return raw;
}

export function mapTypeSenseObjectToGeneric<MapToType>(typeSenseData: TypeSenseSearchResponse<MapToType>): MapToType[] {
  return typeSenseData.hits.map((hit: TypeSenseHit<MapToType>) => {
    const item: Partial<MapToType> = {};
    Object.keys(hit.document).forEach((key: string): void => {
      const itemKey = sItemIntToKey[key];
      if (itemKey === undefined) return;
      let value: unknown = hit.document[key];
      if (keyToType[itemKey] === 'date' && value != null) {
        value = mapTypesenseValueToDate(value);
      }
      if (keyToType[itemKey] === 'string' && value === TYPESENSE_EMPTY_STRING_PLACEHOLDER) {
        value = '';
      }
      (item as Record<string, unknown>)[itemKey] = value;
    });
    (item as Record<string, unknown>)['_tags'] = hit.document['_tags'] ?? [];
    (item as Record<string, unknown>)['multipleSuppliers'] = hit.document['multipleSuppliers'] ?? [];
    return item as MapToType;
  });
}

function escapeString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** Typesense indexes `_stock_take_date` as int64 epoch seconds (not millis like legacy numeric fields). */
const TYPESENSE_STOCK_TAKE_DATE_FIELD = '_stock_take_date';

/**
 * Indexed values at or above this are treated as epoch millis (mixed/legacy indexes).
 * Canonical `_stock_take_date` values are epoch seconds and stay below this floor.
 */
const TYPESENSE_EPOCH_MILLIS_FLOOR = 1_000_000_000_000;

function toTypesenseDateFilterValue(mappedField: string, millis: number): number {
  if (mappedField === TYPESENSE_STOCK_TAKE_DATE_FIELD) {
    return Math.floor(millis / 1000);
  }
  return millis;
}

/**
 * Builds a Typesense filter for "greater/less than (x) days" on a date column.
 * Date comparison (matches filter symbols): less than (x) days = date older than cutoff;
 * greater than (x) days = date newer than cutoff.
 * For `_stock_take_date`, supports seconds and millis indexes. Epoch 0 (1970) is included in
 * older-than via `< cutoff` — do not use `_missing` (requires track_missing_values, causes HTTP 400).
 */
function buildTypesenseDaysAgoDateFilter(mappedField: string, days: number, olderThan: boolean): string {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffMs = cutoff.getTime();
  const cutoffSec = Math.floor(cutoffMs / 1000);
  const field = String(mappedField);

  if (field !== TYPESENSE_STOCK_TAKE_DATE_FIELD) {
    const operator = olderThan ? '<' : '>';
    return `${field}:${operator}${cutoffMs}`;
  }

  if (olderThan) {
    return (
      `(${field}:<${cutoffSec}` +
      ` || (${field}:>=${TYPESENSE_EPOCH_MILLIS_FLOOR} && ${field}:<${cutoffMs}))`
    );
  }

  return (
    `((${field}:>${cutoffSec} && ${field}:<${TYPESENSE_EPOCH_MILLIS_FLOOR})` +
    ` || ${field}:>${cutoffMs})`
  );
}

/**
 * Builds a Typesense filter expression for empty or non-empty field values.
 */
function buildTypesenseEmptyPresenceFilter(mappedField: string | number, columnType: string, isEmpty: boolean): string {
  const field = String(mappedField);

  if (columnType === 'string') {
    if (isEmpty) {
      return `(${field}:_missing || ${field}:=${TYPESENSE_EMPTY_STRING_PLACEHOLDER})`;
    }
    return `(${field}:!_missing && ${field}:!=${TYPESENSE_EMPTY_STRING_PLACEHOLDER})`;
  }

  return isEmpty ? `${field}:_missing` : `${field}:!_missing`;
}

/**
 * Returns true when a filter row has enough data to build a Typesense expression.
 */
/**
 * Returns true when a between filter has both min and max values.
 */
function isBetweenFilterComplete(filter: IFilter): boolean {
  const parts = String(filter.value ?? '').split(',');
  return parts.length === 2 && parts[0].trim() !== '' && parts[1].trim() !== '';
}

function isAdvancedFilterComplete(filter: IFilter): boolean {
  if (!filter.column || !filter.filterType) {
    return false;
  }
  if (filter.filterType === 'is empty' || filter.filterType === 'is not empty') {
    return true;
  }
  if (filter.filterType === 'between') {
    return isBetweenFilterComplete(filter);
  }
  if (filter.columnType === 'boolean') {
    return filter.value !== null && filter.value !== undefined && filter.value !== '';
  }
  return filter.value !== null && filter.value !== undefined && String(filter.value).trim() !== '';
}

function processFilter(filter: IFilter): string {
  if (!isAdvancedFilterComplete(filter)) {
    throw new Error(`Incomplete advanced filter for column: ${filter.column ?? '(none)'}`);
  }

  const symbol = FilterTypeToSymbol[filter.filterType as keyof typeof FilterTypeToSymbol];
  const filterTypesWithoutSymbol = [
    'contains',
    'does not contain',
    'starts with',
    'ends with',
    'is empty',
    'is not empty',
  ];
  if (!symbol && !filterTypesWithoutSymbol.includes(filter.filterType)) {
    throw new Error(`Unsupported filter type: ${filter.filterType}`);
  }

  const fieldKey = filter.column;
  const mappedField = sItemKeyToInt[fieldKey];

  if (mappedField === undefined) {
    throw new Error(`Field ${fieldKey} not found in sItemKeyToInt mapping`);
  }

  const resolvedColumnType = resolveAdvancedFilterColumnType(filter.column, filter.columnType);

  if (filter.filterType === 'is empty') {
    return buildTypesenseEmptyPresenceFilter(mappedField, resolvedColumnType, true);
  }

  if (filter.filterType === 'is not empty') {
    return buildTypesenseEmptyPresenceFilter(mappedField, resolvedColumnType, false);
  }

  if (filter.filterType === 'between') {
    const values = String(filter.value).split(',');
    if (values.length !== 2) {
      throw new Error(`Invalid between value: ${filter.value} for column: ${fieldKey}`);
    }
    let [min, max] = values.map((part: string): string => part.trim());
    if (resolvedColumnType === 'number') {
      if (isNaN(Number(min)) || isNaN(Number(max))) {
        throw new Error(`Invalid between number value: ${filter.value} for column: ${fieldKey}`);
      }
    }
    if (resolvedColumnType === 'date') {
      const minMs = new Date(min).getTime();
      const maxMs = new Date(max).getTime();
      if (Number.isNaN(minMs) || Number.isNaN(maxMs)) {
        throw new Error(`Invalid between date value: ${filter.value} for column: ${fieldKey}`);
      }
      min = String(toTypesenseDateFilterValue(mappedField, minMs));
      max = String(toTypesenseDateFilterValue(mappedField, maxMs));
    }
    return `${mappedField}:[${min}..${max}]`;
  }

  if (filter.filterType === 'is one of' || filter.filterType === 'is not any of') {
    const values = String(filter.value).split(',');
    const formattedValues = values.map(val => {
      if (filter.columnType === 'string') {
        return `"${escapeString(val.trim())}"`;
      }
      return val.trim();
    }).join(',');
    return `${mappedField}:${symbol}[${formattedValues}]`;
  }

  if (filter.filterType === 'contains') {
    const value = escapeString(String(filter.value));
    if (resolvedColumnType === 'string') {
      return `${mappedField}:*${value}*`;
    }
    if (resolvedColumnType === 'string[]') {
      return `${mappedField}:${value}`;
    }
    throw new Error('contains filter type is only supported for string and array columns');
  }

  if (filter.filterType === 'does not contain') {
    const value = escapeString(String(filter.value));
    if (resolvedColumnType === 'string') {
      return `${mappedField}:!${value}`;
    }
    if (resolvedColumnType === 'string[]') {
      return `${mappedField}:!=${value}`;
    }
    throw new Error('does not contain filter type is only supported for string and array columns');
  }

  if (filter.filterType === 'starts with') {
    const value = escapeString(String(filter.value));
    if (resolvedColumnType === 'string') {
      return `${mappedField}:=${value}*`;
    }
    throw new Error('starts with filter type is only supported for string columns');
  }

  if (filter.filterType === 'ends with') {
    throw new Error('ends with filter type must be applied client-side');
  }

  if (
    resolvedColumnType === 'date' &&
    (filter.filterType === 'less than (x) days' || filter.filterType === 'greater than (x) days')
  ) {
    const days = Number(filter.value);
    if (Number.isNaN(days)) {
      throw new Error(`Invalid days value: ${filter.value} for column: ${fieldKey}`);
    }
    const olderThan = filter.filterType === 'less than (x) days';
    return buildTypesenseDaysAgoDateFilter(String(mappedField), days, olderThan);
  }

  let value = filter.value;
  if (resolvedColumnType === 'string') {
    value = `${escapeString(String(value))}`;
  }
  if (resolvedColumnType === 'number') {
    if (isNaN(Number(value))) {
      throw new Error(`Invalid number value: ${value} for column: ${fieldKey}`);
    }
  }
  if (resolvedColumnType === 'boolean') {
    value = String(value).toLowerCase();
  }
  if (resolvedColumnType === 'date') {
    value = toTypesenseDateFilterValue(mappedField, new Date(String(value)).getTime());
  }
  return `${mappedField}:${symbol}${value}`;
}

function processFilterGroup(filterGroup: IFilterGroup, options?: { omitStringPresence?: boolean }): string {
  const operatorSymbol = OperatorMap[filterGroup.operator];
  if (!operatorSymbol) {
    throw new Error(`Unsupported operator: ${filterGroup.operator}`);
  }

  const results: string[] = [];

  for (const item of filterGroup.filtersAndGroups) {
    if ('filtersAndGroups' in item) {
      const nestedResult = processFilterGroup(item, options);
      if (nestedResult.length > 0) {
        results.push(nestedResult);
      }
      continue;
    }

    if (!isAdvancedFilterComplete(item)) {
      continue;
    }

    if (options?.omitStringPresence && isClientSideOnlyAdvancedFilter(item)) {
      continue;
    }

    results.push(processFilter(item));
  }

  if (results.length === 0) {
    if (options?.omitStringPresence) {
      return '';
    }
    throw new Error('Advanced filter group has no complete filter criteria');
  }

  return `(${results.join(` ${operatorSymbol} `)})`;
}

export function convertFilterObjectToTypesenseFilter(
  filterObject: IAdvancedFilterGroup,
  options?: { omitStringPresence?: boolean },
): string {
  if (!filterObject.filterGroup) {
    throw new Error('Invalid filter object: missing filterGroup');
  }
  return processFilterGroup(filterObject.filterGroup, options);
}
